/**
 * PlayerDisplayContext - Global setting for how players are displayed
 * Options: 'number' (jersey number), 'initials' (e.g., EL, OS), or 'position' (e.g., MV, MB)
 * 
 * This setting applies across:
 * - Uppställning (Lineup)
 * - Taktik (Tactics)
 * - Diagram
 * - Animation
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAnchorLabel } from '../lib/positionAnchors';

const STORAGE_KEY = 'trainova_player_display_mode';
const NAME_STORAGE_KEY = 'trainnova_squad_name_mode';

// Display modes
export const DISPLAY_MODES = {
  NUMBER: 'number',
  INITIALS: 'initials',
  POSITION: 'position'
};

// Position labels by game format
export const POSITION_LABELS = {
  '11v11': {
    'MV': 'MV',    // Målvakt
    'GK': 'MV',
    'VB': 'YB',    // Ytterback (vänster)
    'HB': 'YB',    // Ytterback (höger)
    'LB': 'YB',
    'RB': 'YB',
    'MB': 'MB',    // Mittback
    'CB': 'MB',
    'CB1': 'MB',
    'CB2': 'MB',
    'DM': 'DM',    // Defensiv mittfältare
    'CDM': 'DM',
    'CDM1': 'DM',
    'CDM2': 'DM',
    'CM': 'CM',    // Central mittfältare
    'CM1': 'CM',
    'CM2': 'CM',
    'CM3': 'CM',
    'CAM': 'CM',
    'VM': 'YT',    // Ytter (vänster)
    'HM': 'YT',    // Ytter (höger)
    'LM': 'YT',
    'RM': 'YT',
    'VY': 'YT',
    'HY': 'YT',
    'LW': 'YT',
    'RW': 'YT',
    'LWB': 'YB',
    'RWB': 'YB',
    'VYB': 'YB',
    'HYB': 'YB',
    'AN': 'ANF',   // Anfallare
    'ST': 'ANF',
    'ST1': 'ANF',
    'ST2': 'ANF',
    'CF': 'ANF',
    'OM': 'CM'     // Offensiv mittfältare
  },
  '9v9': {
    'MV': 'MV',
    'GK': 'MV',
    'MB': 'B',     // Back
    'CB': 'B',
    'CB1': 'B',
    'CB2': 'B',
    'CB3': 'B',
    'VB': 'B',
    'HB': 'B',
    'LB': 'B',
    'RB': 'B',
    'DM': 'DM',
    'CDM': 'DM',
    'CM': 'CM',
    'CM1': 'CM',
    'CM2': 'CM',
    'VM': 'YM',    // Yttermittfältare
    'HM': 'YM',
    'LM': 'YM',
    'RM': 'YM',
    'AN': 'ANF',
    'ST': 'ANF',
    'ST1': 'ANF',
    'ST2': 'ANF'
  },
  '7v7': {
    'MV': 'MV',
    'GK': 'MV',
    'MB': 'B',
    'CB': 'B',
    'CB1': 'B',
    'CB2': 'B',
    'CB3': 'B',
    'VB': 'B',
    'HB': 'B',
    'LB': 'B',
    'RB': 'B',
    'DM': 'MF',    // Mittfältare
    'CM': 'MF',
    'CM1': 'MF',
    'CM2': 'MF',
    'VM': 'MF',
    'HM': 'MF',
    'LM': 'MF',
    'RM': 'MF',
    'AN': 'ANF',
    'ST': 'ANF'
  },
  '5v5': {
    'MV': 'MV',
    'GK': 'MV',
    'MB': 'B',
    'CB': 'B',
    'CB1': 'B',
    'CB2': 'B',
    'DM': 'MF',
    'CM': 'MF',
    'VM': 'MF',
    'HM': 'MF',
    'LM': 'MF',
    'RM': 'MF',
    'AN': 'ANF',
    'ST': 'ANF'
  }
};

const PlayerDisplayContext = createContext();

export function PlayerDisplayProvider({ children }) {
  // 'number' = jersey number, 'initials' = player initials, 'position' = position label
  const [displayMode, setDisplayMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) || 'number';
    }
    return 'number';
  });

  // Independent NAME sub-label under the marker: 'off' | 'first_name' | 'last_name'.
  // Display-only preference — persisted locally, NEVER mixed with lineup business data.
  const [nameMode, setNameMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const v = localStorage.getItem(NAME_STORAGE_KEY);
      return (v === 'first_name' || v === 'last_name') ? v : 'off';
    }
    return 'off';
  });

  // Persist to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, displayMode);
    }
  }, [displayMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(NAME_STORAGE_KEY, nameMode);
    }
  }, [nameMode]);

  // Cycle through modes: number -> initials -> position -> number
  const cycleDisplayMode = () => {
    setDisplayMode(prev => {
      if (prev === 'number') return 'initials';
      if (prev === 'initials') return 'position';
      return 'number';
    });
  };

  return (
    <PlayerDisplayContext.Provider value={{ displayMode, setDisplayMode, cycleDisplayMode, nameMode, setNameMode }}>
      {children}
    </PlayerDisplayContext.Provider>
  );
}

export function usePlayerDisplay() {
  const context = useContext(PlayerDisplayContext);
  if (!context) {
    throw new Error('usePlayerDisplay must be used within a PlayerDisplayProvider');
  }
  return context;
}

// Helper function to get display text for a player
export function getPlayerDisplayText(player, mode = 'number', gameFormat = '11v11', formationPointId = null) {
  if (mode === 'initials') {
    // Get initials from name (e.g., "Erik Lindberg" -> "EL")
    const name = player?.name || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else if (parts.length === 1 && parts[0].length >= 2) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return '??';
  }
  
  if (mode === 'position') {
    // Get position label based on formation point or player position
    const labels = POSITION_LABELS[gameFormat] || POSITION_LABELS['11v11'];
    const labels11 = POSITION_LABELS['11v11'];

    // Try formation point / semantic anchor ID first (most accurate).
    if (formationPointId) {
      // Semantic anchor (lowercase id) → precise label from the anchor registry.
      const anchorLabel = getAnchorLabel(formationPointId);
      if (anchorLabel) return anchorLabel;
      // Legacy formation-point ids (uppercase, e.g. CM1 / LWB).
      if (labels[formationPointId]) return labels[formationPointId];
      const up = String(formationPointId).toUpperCase();
      if (labels[up]) return labels[up];
      if (labels11[up]) return labels11[up];
    }
    
    // Try player's position field
    const playerPos = player?.position?.toUpperCase() || '';
    if (labels[playerPos]) return labels[playerPos];
    
    // Try to extract from role
    const role = player?.role?.toUpperCase() || '';
    if (labels[role]) return labels[role];
    
    return '?';
  }
  
  // Default: jersey number
  return player?.jersey_number?.toString() || '?';
}

// Resolve the NAME sub-label ('off'|'first_name'|'last_name') from existing
// roster fields only. Never returns undefined/null/"Unknown"; hides ('') when
// nothing safe resolves.
export function getPlayerNameText(player, nameMode = 'off') {
  if (!player || !nameMode || nameMode === 'off') return '';
  const clean = (s) =>
    (typeof s === 'string' && s.trim() && !/^(undefined|null|unknown|okänd)$/i.test(s.trim()))
      ? s.trim() : null;
  const first = clean(player.first_name) || clean(player.firstName);
  const last = clean(player.last_name) || clean(player.lastName);
  const full = clean(player.name) || clean(player.full_name) || clean(player.display_name);
  if (nameMode === 'first_name') {
    if (first) return first;
    if (full) return full.split(/\s+/)[0];
    return '';
  }
  if (nameMode === 'last_name') {
    if (last) return last;
    if (full) { const p = full.split(/\s+/); return p.length > 1 ? p[p.length - 1] : p[0]; }
    return '';
  }
  return '';
}

export default PlayerDisplayContext;
